import type { FmDashboardOverviewProps } from './FmDashboardOverview.types';
import { array } from './FmDashboardOverview.validationShared';
import {
  validateCalmAnswerSection,
  validateHandlers,
  validateHeaderActions,
} from './FmDashboardOverview.regionValidation';
import { validateRankedCollection } from './FmDashboardOverview.recordValidation';
import {
  dashboardExact as exact,
  dashboardRecord as record,
  dashboardString as string,
  dashboardValidationFail as fail,
} from './FmDashboardOverview.validationHelpers';

/** Enforce the sealed dashboard descriptor contract before any renderer work begins. */
export function validateFmDashboardOverviewProps(
  value: unknown,
): asserts value is FmDashboardOverviewProps {
  const props = record(value, 'props');
  exact(
    props,
    [
      'title',
      'subtitle',
      'introduction',
      'ariaLabel',
      'headerActions',
      'sections',
      'handlers',
      'data-testid',
    ],
    'props',
  );
  string(props.title, 'props.title');
  if (props.subtitle !== undefined) string(props.subtitle, 'props.subtitle');
  if (props.introduction !== undefined) {
    const introduction = record(props.introduction, 'props.introduction');
    exact(introduction, ['description', 'actionTitle', 'actionHint'], 'props.introduction');
    string(introduction.description, 'props.introduction.description');
    string(introduction.actionTitle, 'props.introduction.actionTitle');
    string(introduction.actionHint, 'props.introduction.actionHint');
    if (props.headerActions === undefined) fail('props.introduction', 'requires header actions');
  }
  string(props.ariaLabel, 'props.ariaLabel');
  if (props['data-testid'] !== undefined) string(props['data-testid'], 'props.data-testid');

  const handlers = validateHandlers(props.handlers);
  const ids = new Set<string>();
  validateHeaderActions(props.headerActions, ids);
  const sections = array(props.sections, 'props.sections');
  if (sections.length !== 1 && sections.length !== 2) {
    fail(
      'props.sections',
      'must contain one ranked collection or a calm answer followed by one ranked collection',
    );
  }

  const answer = sections.length === 2 ? record(sections[0], 'props.sections[0]') : undefined;
  const calm = answer ? validateCalmAnswerSection(answer, ids, handlers) : null;
  const collectionIndex = answer ? 1 : 0;
  const collection = validateRankedCollection(
    sections[collectionIndex],
    `props.sections[${collectionIndex}]`,
    ids,
    handlers,
  );
  if (!calm) return;
  if (collection.rankingRunId !== calm.rankingRunId) {
    fail(`props.sections[${collectionIndex}].rankingRunId`, 'must match answer rankingRunId');
  }
  if (collection.topRecordId !== calm.topRecordId) {
    fail(`props.sections[${collectionIndex}].topRecordId`, 'must match answer topRecordId');
  }
}
